/**
 * Composing a description — the plumbing behind "examine works on everything"
 * (design spec pillar 4).
 *
 * This lives in the sim, beside the entity and terrain definitions, rather
 * than in the server or the client. That placement is the whole point of the
 * ticket: **content describes itself**. A tile added by M3-01's worldgen, a
 * wound added by M1-06, a trophy added by M5 — each brings its own prose with
 * its definition, and this file's job is only to put the pieces in order.
 * Nothing here enumerates content, so nothing here has to be revisited when
 * content arrives. A `switch` over tile kinds in the client would have had to
 * be, every single time.
 *
 * A description is composed, not looked up: terrain, then what the terrain is
 * like, then what is standing on it. The M2-06 body diagram slots into
 * `describeActor` and needs no other change.
 */
import { partFactor } from './body.js';
import { kitOf, shieldConditionAt, shieldOf } from './fighter.js';
import { floraKey } from './flora.js';
import { bestSignAt, describeSign } from './sign.js';
import { describeFlora } from './forage.js';
import { isSheltered, tileAt, tileDefAt } from './map.js';
import { actorAt } from './movement.js';
function describe(lines, refs) {
    const kept = lines.filter((line) => line.length > 0);
    return { lines: kept, refs, text: kept.join('\n') };
}
/** Terrain, in two sentences: what it is, and what it is like. */
export function describeTile(world, x, y) {
    const kind = tileAt(world.map, x, y);
    const def = tileDefAt(world.map, x, y);
    return { lines: [capitalize(def.label) + '.', def.detail], kind };
}
/* -------------------------------------------------------------------------- *
 * Kit (M2-04)
 * -------------------------------------------------------------------------- */
/** How a shield in each state reads to somebody looking at it. */
const SHIELD_LOOK = {
    sound: 'sound',
    notched: 'notched around the rim',
    split: 'split across the boss',
    broken: 'broken past use',
};
/** What an edge in each state reads as. `keen` is unremarkable and unremarked. */
const EDGE_LOOK = {
    keen: '',
    worn: 'The edge has gone dull with use.',
    blunted: 'The edge is blunted; it wants a whetstone badly.',
};
/**
 * What somebody is carrying, as prose — the M2-04 half of "examine works on
 * everything".
 *
 * Everything here is *visible*: what is in the hands, what is on the body, and
 * what state the shield is in, because a man behind a split board is
 * information his opponent has earned. The edge on his blade is not, and only
 * `describeSelf` reads it.
 *
 * Nothing enumerates content. A weapon added to the kit describes itself
 * through the `description` it brought with it, which is the rule this whole
 * module exists to keep.
 */
export function describeKit(catalogue, combatant, options = {}) {
    const kit = kitOf(catalogue, combatant);
    if (!kit)
        return [];
    const held = options.own ? 'You are holding' : 'Holding';
    const lines = [`${held} ${kit.weapon.name}.`];
    if (options.own) {
        const edge = EDGE_LOOK[combatant.loadout.edge ?? 'keen'];
        if (edge)
            lines.push(edge);
    }
    // The clothing layer counts as worn: a wool tunic is worth no armour value
    // and is still the honest answer to "what is he wearing".
    const worn = kit.armour.filter((piece) => (piece.defenceDice ?? 0) === 0);
    if (worn.length > 0) {
        const subject = options.own ? 'You are wearing' : 'Wearing';
        lines.push(`${subject} ${list(worn.map((piece) => piece.name))}.`);
    }
    const shield = shieldOf(kit.armour);
    if (shield) {
        const state = SHIELD_LOOK[shieldConditionAt(combatant.shieldWear)] ?? 'sound';
        const subject = options.own ? 'Your' : 'The';
        lines.push(`${subject} ${shield.name.replace(/^an? /, '')} is ${state}.`);
    }
    return lines;
}
/** `['a', 'b', 'c']` → `'a, b and c'`. The log is prose; lists are not commas. */
function list(items) {
    if (items.length <= 1)
        return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
/**
 * Somebody else, seen from outside.
 *
 * Deliberately thin: everything a stranger can tell by looking. M1's wounds
 * and M2-06's body diagram add lines here — visible injuries are public
 * information, which is what makes a limp worth reading — and nothing that is
 * not visible ever should.
 */
export function describeActor(actor, catalogue, combatant) {
    const article = actor.kind === 'player' ? '' : 'A ';
    const posture = combatant?.vitals.prone === true ? 'is down' : 'is standing here';
    const lines = [`${article}${actor.name} ${posture}, facing ${bearing(actor.facing)}.`];
    if (catalogue && combatant) {
        lines.push(...describeKit(catalogue, combatant));
        lines.push(...observableCondition(catalogue, combatant, actor.name));
    }
    return lines;
}
/**
 * What a stranger can *see* of somebody's condition — M2-06's hidden-information
 * stance, applied to the thing it is hardest to apply it to.
 *
 * The rule is M1-05's: an onlooker learns what an onlooker could learn. A limp,
 * a red muzzle, an arm that is not being used. Never a wound level, never a
 * blood figure, never whether the infection has turned — those are the owner's,
 * and a client that could read them off a stranger would be a client playing
 * odds no person standing there could.
 *
 * Which is also why this reads bodies rather than wound records: a destroyed
 * part shows, and a nasty gash under a tunic does not.
 */
export function observableCondition(catalogue, combatant, name) {
    const kit = kitOf(catalogue, combatant);
    if (!kit)
        return [];
    const lines = [];
    const { body } = combatant.vitals;
    const their = combatant.pronouns.possessive;
    // Bleeding is the loudest visible fact there is, and the one that decides
    // whether a wounded animal is worth following into the wood.
    if (combatant.vitals.wounds.some((wound) => wound.bleedRate > 0)) {
        lines.push(`${name} is bleeding.`);
    }
    const failing = [];
    for (const part of kit.plan.parts) {
        const state = body.parts[part.id];
        if (!state)
            continue;
        if (state.destroyed) {
            lines.push(`${capitalize(their)} ${part.name} is gone.`);
        }
        else if (partFactor(state) < IMPAIRED_VISIBLY) {
            failing.push(`${their} ${part.name}`);
        }
    }
    // Collected into one clause: four sentences about four bad legs reads as a
    // list, and what a person actually notices is that it is favouring a side.
    if (failing.length > 0)
        lines.push(`${name} favours ${list(failing)}.`);
    return lines;
}
/** Below this, a part's loss of function is something an onlooker can see. */
export const IMPAIRED_VISIBLY = 0.7;
/**
 * Yourself. A stub character sheet until M2-06 fleshes it out with the body
 * diagram; non-empty from day one because "examine works on everything"
 * includes the thing doing the examining.
 */
export function describeSelf(actor, catalogue, combatant) {
    const lines = [
        `${actor.name}. You are on your feet, facing ${bearing(actor.facing)}.`,
        'You are unhurt, so far as you can tell.',
    ];
    const kit = catalogue && combatant ? describeKit(catalogue, combatant, { own: true }) : [];
    lines.push(...(kit.length > 0 ? kit : ['You carry nothing worth naming.']));
    return lines;
}
/** The tick, for the layers that read an age off one. `0` on a partial world. */
function tickOf(world) {
    return world.tick ?? 0;
}
/**
 * The layers, in reading order (M3-04).
 *
 * Order is the design here, and it is the order somebody standing there would
 * notice things in: the ground, then what the ground is like today, then what
 * is growing on it, then what is lying on it, then who is standing on it. A
 * player reads the first line most and the last line when it matters.
 *
 * This is a list rather than a sequence of `if` blocks so that a later
 * milestone adds a clause by adding an entry. M3-08's tracks are the next one
 * and they slot in after `carcasses` and before `occupant` — a print is on the
 * ground and a person is above it.
 */
export const DESCRIBE_LAYERS = [
    // The ground itself: what it is, and what it is like underfoot. Both
    // sentences come off the tile definition, so a terrain added by worldgen
    // describes itself the moment it exists.
    ({ world, x, y }) => {
        const tile = describeTile(world, x, y);
        const def = tileDefAt(world.map, x, y);
        return { lines: tile.lines, refs: [String(x), String(y), tile.kind, def.material] };
    },
    // What the weather has done to it (M3-04, reading M3-05).
    //
    // The materials layer proving it earns its keep: whether ground is wet is a
    // question about what it is *made of* and what the sky has been doing, and
    // answering it here means no tile has to carry a "sodden" variant of its own
    // prose. It is also the clause that makes M3-06's fire-lighting legible —
    // a player who has been told the peat is sodden is not surprised when the
    // tinder will not take.
    ({ world, x, y }) => {
        const weather = world.weather;
        if (!weather)
            return null;
        const clause = groundWetness(world, x, y, weather);
        return clause ? { lines: [clause], refs: ['wet'] } : null;
    },
    // Between the ground and whoever is standing on it (M3-03). A plant is a
    // thing *on* a tile, so it reads after what the tile is and before who is
    // there — which is also the order you would notice them in.
    ({ world, x, y, viewerId, catalogue }) => {
        const patch = world.flora?.[floraKey(x, y)];
        if (!patch || !catalogue)
            return null;
        return {
            lines: [describeFlora(catalogue, patch, viewerId ? world.lore?.[viewerId] : undefined)],
            refs: [patch.speciesId],
        };
    },
    // A fire, which is the one thing on a tile that changes what the tile is for.
    ({ world, x, y }) => {
        const lights = world.lights;
        if (!lights)
            return null;
        const burning = Object.values(lights).some((light) => light.x === x && light.y === y && light.carriedBy === null);
        return burning ? { lines: ['A fire burns here.'], refs: ['fire'] } : null;
    },
    // What is dead on it (M2-05/M3-06). Before the occupant, because a body on
    // the ground is beneath somebody standing over it.
    ({ world, x, y }) => {
        const carcasses = world.carcasses;
        if (!carcasses)
            return null;
        const carcass = Object.values(carcasses).find((body) => body.x === x && body.y === y);
        if (!carcass)
            return null;
        return { lines: [`The body of ${carcass.name} lies here.`], refs: [carcass.speciesId] };
    },
    // What has passed this way (M3-08). After the carcass and before the
    // occupant, which is the slot the layered describer was built to have open:
    // a print is on the ground and a person is above it.
    //
    // Only the *best* mark on the tile, not all four. An examine that read out a
    // print, a trail, droppings and blood in four sentences would bury the one
    // that matters under the three that do not — and blood is always the one
    // that matters, because it means something is hurt.
    ({ world, x, y, viewerId, catalogue }) => {
        const signs = world.signs;
        const weather = world.weather;
        if (!signs || !weather)
            return null;
        const sign = bestSignAt(signs, x, y);
        if (!sign)
            return null;
        const familiarity = viewerId ? (world.lore?.[viewerId]?.tracking ?? 0) : 0;
        const species = catalogue?.plan(sign.speciesId)?.name;
        return {
            lines: [describeSign(sign, tickOf(world), weather, familiarity, species)],
            refs: ['sign', sign.kind, sign.speciesId, sign.direction],
        };
    },
    // Whoever is standing on it. Last, and longest — the body diagram lives at
    // the end of an examine because it is what you read when it matters.
    ({ world, x, y, viewerId, catalogue }) => {
        const occupant = actorAt(world, x, y);
        if (!occupant)
            return null;
        const combatant = world.combat?.[occupant.id];
        return {
            lines: occupant.id === viewerId
                ? describeSelf(occupant, catalogue, combatant)
                : describeActor(occupant, catalogue, combatant),
            refs: [occupant.id],
        };
    },
];
/**
 * How wet this ground is, in a clause, or `null` when there is nothing to say.
 *
 * Reads the *material* rather than the tile kind, which is the whole argument
 * for materials-lite: peat holds water and rock sheds it, and that is true of
 * every peat tile M3-01 ever adds without any of them being listed here.
 */
function groundWetness(world, x, y, weather) {
    const material = tileDefAt(world.map, x, y).material;
    if (material === 'nothing' || material === 'water')
        return null;
    const wet = weather.wet / 1000;
    // A floor is authored interior terrain: rain may leave the air and rushes
    // damp, but it cannot turn the ground under a sound roof into standing
    // water. Keep the description about what the player can actually feel here,
    // rather than repeating the valley-wide wetness word indoors.
    if (isSheltered(world.map, x, y)) {
        return wet > 0.45 ? 'The floor is damp beneath the roof.' : null;
    }
    // Ground that is wet by its nature says so whatever the sky is doing, and
    // says it more strongly after rain.
    if (material === 'peat') {
        return wet > 0.5 ? 'The peat is waterlogged, and gives underfoot.' : 'The peat is damp.';
    }
    if (wet > 0.75)
        return 'The ground is sodden; water stands in every hollow.';
    if (wet > 0.45)
        return 'The ground is wet.';
    if (wet < 0.15 && (material === 'plant' || material === 'earth' || material === 'sand')) {
        return 'It is bone dry, and would take a light.';
    }
    return null;
}
/**
 * The full answer to "what is over there".
 *
 * A fold over `DESCRIBE_LAYERS` rather than a sequence of `if` blocks, since
 * M3-04: what changed is not what it says but that *adding to* what it says is
 * a one-entry change in a list, made by the system that has something to add.
 * The alternative — this function growing a branch per milestone — is the
 * `switch` over tile kinds that M0-06's header already refused once.
 *
 * `viewerId` changes only the last layer: looking at your own tile reads out
 * the character sheet rather than a stranger's silhouette.
 */
export function describeAt(world, x, y, viewerId, catalogue) {
    const context = { world, x, y, viewerId, catalogue };
    const lines = [];
    const refs = [];
    for (const layer of DESCRIBE_LAYERS) {
        const part = layer(context);
        if (!part)
            continue;
        if (part.lines)
            lines.push(...part.lines);
        if (part.refs)
            refs.push(...part.refs);
    }
    return describe(lines, refs);
}
const BEARINGS = {
    n: 'north',
    ne: 'north-east',
    e: 'east',
    se: 'south-east',
    s: 'south',
    sw: 'south-west',
    w: 'west',
    nw: 'north-west',
};
/** `'ne'` → `'north-east'`. The log is prose; compass codes are not. */
export function bearing(direction) {
    return BEARINGS[direction] ?? direction;
}
function capitalize(text) {
    return text.slice(0, 1).toUpperCase() + text.slice(1);
}
//# sourceMappingURL=describe.js.map